/**
 * smoothScroll — single source of truth for in-page anchor scrolling.
 *
 * Why a shared module?
 *   Previously Navbar.jsx and App.js (Home's hash-restore effect) each
 *   ran their OWN convergence loop with its own ResizeObserver, wheel/
 *   touchmove/keydown listeners, and 6-second polling window. Clicking
 *   two nav links in quick succession (or clicking a tab while the
 *   initial hash effect was still running) stacked them. The two loops
 *   then re-applied scrollTo on every observed layout shift, fighting
 *   each other and the user. Symptoms reported:
 *     • "page keeps jumping back when I try to scroll"
 *     • "click does nothing for a second then jerks"
 *     • "back button drops me at the top"
 *
 *   This module enforces a single active scroll job. Calling
 *   `smoothScrollToHash(href)` while another job is running cancels the
 *   prior job before starting the new one. All convergence-loop pollers,
 *   ResizeObservers, and interrupt listeners live in one place.
 */

const NAV_OFFSET = 80;          // height of the fixed navbar in px
const SAFETY_MS = 2000;         // hard cap on total convergence time.
                                // Reduced from 2500ms — anything longer
                                // and an interrupted user gets visibly
                                // tugged for too long on slow networks.
const POLL_MS = 120;            // gap between target re-checks
const RO_DEBOUNCE_MS = 120;     // debounce ResizeObserver bursts
const STABLE_SAMPLES = 3;       // exit early after N samples within 2px
const STABLE_EPSILON = 2;       // px

let activeJob = null;

/** True while a programmatic smooth-scroll job is in flight. Read by
 *  the Navbar scroll-spy so it suspends URL-hash sync during the
 *  convergence loop — otherwise the spy would replaceState every
 *  transitional section we pass through and overwrite the pushState
 *  we just made at click time. */
export function isScrolling() { return activeJob !== null; }

export function cancelActiveScroll() {
  if (!activeJob) return;
  activeJob.cancel();
  activeJob = null;
}

/**
 * Smooth-scroll to a `#section-id` anchor with a navbar offset and a
 * convergence loop that re-targets on layout shifts. Returns true if
 * the target was found and a scroll was queued, false otherwise.
 *
 * @param {string} hash - href like "#memberships" (with the leading '#')
 * @param {object} [opts]
 * @param {'push'|'replace'|'none'} [opts.history='push'] - history update mode
 *
 * History semantics:
 *   'push'    — DEFAULT. Creates a new browser history entry. This is
 *               what every explicit user navigation needs (navbar
 *               tab click, in-section CTA <a href="#X">) so that
 *               pressing BACK returns to the PREVIOUS section.
 *   'replace' — Updates the current entry without creating a new
 *               one. Used by the Navbar scroll-spy as the user
 *               passively scrolls between sections — we don't want
 *               every section the user drifts over to become a
 *               separate BACK stop.
 *   'none'    — Don't touch history at all. Used by Home's hash
 *               handler on POP, since the browser already updated
 *               the URL during the back nav.
 */
export function smoothScrollToHash(hash, opts = {}) {
  if (typeof window === 'undefined' || !hash || hash.length < 2) return false;
  const id = hash.charAt(0) === '#' ? hash.slice(1) : hash;
  let el = document.getElementById(id);

  // Target not in DOM yet — common right after browser BACK / FORWARD
  // when the previous route remounts and its lazy-loaded section
  // children are still hydrating. Schedule a few retries before giving
  // up. Stops the moment the user scrolls (so we never fight a user
  // gesture) or after 1 s total.
  if (!el) {
    const { history: historyMode = 'push' } = opts;
    cancelActiveScroll();
    if (historyMode !== 'none' && window.history) {
      try {
        const fn = historyMode === 'push' ? 'pushState' : 'replaceState';
        const currentHash = window.location.hash;
        if (historyMode === 'replace' || currentHash !== `#${id}`) {
          window.history[fn](null, '', `#${id}`);
          window.dispatchEvent(new HashChangeEvent('hashchange'));
        }
      } catch (_e) { /* ignore */ }
    }
    let retries = 0;
    let userTouched = false;
    const onUserScroll = () => { userTouched = true; };
    const events = ['wheel', 'touchmove', 'pointerdown'];
    events.forEach((ev) => {
      window.addEventListener(ev, onUserScroll, { passive: true, once: true });
    });
    const tryAgain = () => {
      if (userTouched) {
        events.forEach((ev) => window.removeEventListener(ev, onUserScroll));
        return;
      }
      const found = document.getElementById(id);
      if (found) {
        events.forEach((ev) => window.removeEventListener(ev, onUserScroll));
        // Retarget without re-applying history (we already did above).
        smoothScrollToHash(hash, { history: 'none' });
        return;
      }
      if (++retries < 10) setTimeout(tryAgain, 100);
      else {
        events.forEach((ev) => window.removeEventListener(ev, onUserScroll));
      }
    };
    setTimeout(tryAgain, 100);
    return false;
  }

  const { history: historyMode = 'push' } = opts;

  cancelActiveScroll();

  if (historyMode !== 'none' && window.history) {
    try {
      const fn = historyMode === 'push' ? 'pushState' : 'replaceState';
      // Only push if the target differs from the current URL — clicking
      // the SAME nav item twice should NOT add duplicate history entries.
      const currentHash = window.location.hash;
      if (historyMode === 'replace' || currentHash !== `#${id}`) {
        window.history[fn](null, '', `#${id}`);
        // Dispatch popstate-like event so react-router useLocation picks
        // up the hash change. pushState/replaceState don't fire popstate
        // natively, but firing a hashchange does trigger useLocation
        // updates in react-router v6.
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      }
    } catch (_e) { /* ignore */ }
  }

  const targetTop = () => el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;

  // Initial smooth kick.
  try {
    window.scrollTo({ top: targetTop(), behavior: 'smooth' });
  } catch (_e) {
    window.scrollTo(0, targetTop());
  }

  let cancelled = false;
  let userInterrupted = false;
  const start = performance.now();
  let stableHits = 0;
  let lastY = window.scrollY;
  let pollTimer = 0;
  let roTimer = 0;
  let resizeObserver = null;
  // Hard cap on ResizeObserver-triggered re-snaps. Defense in depth
  // against any runaway feedback loop (e.g., iOS Safari URL bar
  // oscillation triggering main height changes which trigger reSnap
  // which triggers scrollTo which triggers URL bar bounce…).
  let reSnapCount = 0;
  const MAX_RESNAPS = 8;

  const interrupt = () => { userInterrupted = true; };
  // Wheel + touchmove + pointerdown + keydown all signal the user is
  // taking control. We listen passively (no preventDefault) so we
  // never block native scrolling. We deliberately DON'T listen to
  // `touchstart` — iOS Safari fires touchstart at the start of EVERY
  // tap on the page (including taps on buttons, links, the navbar),
  // and adding a forced `scrollTo` halt inside this callback was
  // breaking iOS Safari's native scroll gesture entirely.
  window.addEventListener('wheel', interrupt, { passive: true });
  window.addEventListener('touchmove', interrupt, { passive: true });
  window.addEventListener('pointerdown', interrupt, { passive: true });
  window.addEventListener('keydown', interrupt);

  const cleanup = () => {
    cancelled = true;
    window.removeEventListener('wheel', interrupt);
    window.removeEventListener('touchmove', interrupt);
    window.removeEventListener('pointerdown', interrupt);
    window.removeEventListener('keydown', interrupt);
    if (pollTimer) { clearTimeout(pollTimer); pollTimer = 0; }
    if (roTimer) { clearTimeout(roTimer); roTimer = 0; }
    if (resizeObserver) { resizeObserver.disconnect(); resizeObserver = null; }
    if (activeJob && activeJob.id === jobId) activeJob = null;
  };

  const reSnap = () => {
    if (cancelled || userInterrupted) return;
    if (reSnapCount >= MAX_RESNAPS) return;
    reSnapCount += 1;
    const cur = targetTop();
    if (Math.abs(cur - window.scrollY) > STABLE_EPSILON) {
      window.scrollTo({ top: cur, behavior: 'auto' });
    }
  };

  const tick = () => {
    if (cancelled || userInterrupted) { cleanup(); return; }
    const now = performance.now();
    const cur = targetTop();
    const drift = Math.abs(cur - window.scrollY);
    const moved = Math.abs(window.scrollY - lastY) > STABLE_EPSILON;
    lastY = window.scrollY;
    if (drift > STABLE_EPSILON) {
      window.scrollTo({ top: cur, behavior: 'auto' });
      stableHits = 0;
    } else if (!moved) {
      stableHits += 1;
    }
    if (stableHits >= STABLE_SAMPLES) { cleanup(); return; }
    if (now - start >= SAFETY_MS) { cleanup(); return; }
    pollTimer = setTimeout(tick, POLL_MS);
  };

  // ResizeObserver — debounced — re-snaps on actual layout shifts.
  // Disabled on coarse-pointer devices (touch / iOS Safari): the URL
  // bar collapse/expand during scroll constantly resizes the viewport,
  // which would trigger reSnap → scrollTo → more URL-bar oscillation,
  // building memory/paint pressure that crashes iOS Safari (the
  // "A problem repeatedly occurred" tab kill). On coarse-pointer we
  // rely on the initial smooth-kick + the polling tick alone, which
  // is sufficient because mobile pages have less lazy-content geometry
  // shift than desktop.
  const skipResizeObserver = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  if (!skipResizeObserver && typeof ResizeObserver !== 'undefined') {
    const main = document.querySelector('main') || document.body;
    if (main) {
      resizeObserver = new ResizeObserver(() => {
        if (cancelled || userInterrupted) return;
        if (performance.now() - start >= SAFETY_MS) return;
        if (reSnapCount >= MAX_RESNAPS) return;
        if (roTimer) clearTimeout(roTimer);
        roTimer = setTimeout(() => { roTimer = 0; reSnap(); }, RO_DEBOUNCE_MS);
      });
      resizeObserver.observe(main);
    }
  }

  const jobId = Symbol('scroll-job');
  activeJob = { id: jobId, cancel: cleanup };
  pollTimer = setTimeout(tick, 180); // short head-start for the smooth kick
  return true;
}
